/**
 * ShareCodeModal
 * Export current config as a shareable code string
 */

import React, { useState, useMemo } from 'react';
import { TemplateConfig } from '@/types/template.types';
import { useShareCode } from '@hooks/useShareCode';
import { copyToClipboard } from '@/utils/clipboard';
import { Modal } from '../ui';
import { Button } from '../ui';
import { Copy, CheckCircle2, Save } from 'lucide-react';

interface ShareCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: TemplateConfig;
}

export const ShareCodeModal: React.FC<ShareCodeModalProps> = ({ isOpen, onClose, config }) => {
  const { encode, savePreset } = useShareCode();
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [presetName, setPresetName] = useState('');

  const code = useMemo(() => encode(config), [config, encode]);

  const handleCopy = async () => {
    try {
      await copyToClipboard(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSavePreset = () => {
    if (!presetName.trim()) return;
    savePreset(presetName.trim(), config);
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      setPresetName('');
    }, 2000);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Share Configuration">
      <div className="space-y-4">
        {/* Code Display */}
        <div>
          <label htmlFor="share-code-output" className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2 block">
            Share Code
          </label>
          <div className="relative">
            <textarea
              id="share-code-output"
              readOnly
              value={code}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-[11px] text-cyan-400 font-mono resize-none h-24 focus:outline-none"
              onClick={e => (e.target as HTMLTextAreaElement).select()}
            />
            <div className="absolute bottom-2 right-2 flex items-center gap-2">
              <span className="text-[11px] text-slate-700">{code.length} chars</span>
            </div>
          </div>
        </div>

        {/* Copy Button */}
        <Button
          variant={copied ? 'success' : 'primary'}
          size="sm"
          onClick={handleCopy}
          className="w-full !rounded-xl !text-xs !font-black !uppercase !tracking-widest"
        >
          {copied ? (
            <><CheckCircle2 size={14} className="mr-1.5" /> Copied!</>
          ) : (
            <><Copy size={14} className="mr-1.5" /> Copy to Clipboard</>
          )}
        </Button>

        {/* Save as Preset */}
        <div className="pt-2 border-t border-slate-800">
          <label htmlFor="preset-name-input" className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2 block">
            Save as Local Preset
          </label>
          <div className="flex gap-2">
            <input
              id="preset-name-input"
              type="text"
              value={presetName}
              onChange={e => setPresetName(e.target.value)}
              placeholder="Preset name..."
              className="flex-1 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-300 placeholder:text-slate-700 focus:border-accent/50 outline-none"
              onKeyDown={e => { if (e.key === 'Enter') handleSavePreset(); }}
            />
            <Button
              variant={saved ? 'success' : 'secondary'}
              size="sm"
              onClick={handleSavePreset}
              disabled={!presetName.trim() || saved}
            >
              {saved ? <CheckCircle2 size={14} /> : <Save size={14} />}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
